export const MANIFEST_WORKBOOK_INSTRUCTIONS = [
  'Use the same case label to group slides into one case. Case ID is that label, not a numeric saved-case identifier. Do not change a saved case label to rename the case.',
  'Choose New case from the New case column dropdown to create a case. Leave that cell blank to select the one saved case with the same label. Any other value is invalid. Duplicate saved labels stay blocked until you correct them.',
  'Case label, Stain Type, Cancer Type, and a Slide ID or File Name are required. Biopsy Site, Cancer Subtype, Biopsy Type, Preservation Method, Procedure Type, and the Y/N columns are optional.',
  'Cancer Type, Cancer Subtype, Biopsy Site, and Biopsy Type must agree for every slide with the same case label. Correct a conflict yourself. The file does not choose or copy one row over another.',
  'Stain Type, File Name, Preservation Method, Procedure Type, Metastasis, and the control columns apply to each slide.',
  'A blank Cancer Type remains N/A. A blank subtype or other slide-metadata cell keeps the existing value. A blank cell does not clear a saved value.',
  'References lists the choices available at download. An empty dropdown means those choices were unavailable. Do not invent a value. Case ID and Cancer Subtype stay free text.',
  'A populated Slide ID is used alone. File Name is used only when Slide ID is blank, and only when that name matches one slide in this lab.',
  'Upload replaces the unsaved case assignment. Saved assignments change only after you submit changes, review them, and apply.',
] as const;

export const MANIFEST_TABLE_FIELD_GUIDANCE = [
  'Case label, Stain Type, Cancer Type, and a slide identifier are required.',
  "Cancer Type, Cancer Subtype, Biopsy Site, and Biopsy Type must be the same for every slide in a case. Other metadata applies to each slide. Correct a conflict before submitting changes. One slide's value is not copied to the others.",
] as const;

export const MANIFEST_EXCEL_FIELD_GUIDANCE =
  'Use the same case label to group slides. Case ID is the label, not a saved-case number. Choose New case to create a case, or leave that cell blank to select the one saved case with the same label. Case label, Stain Type, Cancer Type, and a slide identifier are required. Cancer Type, Cancer Subtype, Biopsy Site, and Biopsy Type must agree for that case; other metadata applies to each slide. The Instructions sheet lists saved cases, and References lists the choices available at download. Edit the file externally, then upload the complete Excel or CSV manifest.';

const savedCaseLabel = (label: string | null | undefined) => {
  const visibleLabel = label?.replace(/\s+/g, ' ').trim();
  return visibleLabel ? visibleLabel : '(no label)';
};

export const buildManifestWorkbookInstructions = (
  existingCases: readonly { id?: number | null; label?: string | null }[]
): string[] => {
  const savedCases = existingCases
    .filter(
      (procedure): procedure is { id: number; label?: string | null } =>
        Number.isInteger(procedure.id) && (procedure.id as number) > 0
    )
    .map((procedure) => ({ id: procedure.id, label: savedCaseLabel(procedure.label) }))
    .sort((left, right) => left.id - right.id || left.label.localeCompare(right.label));
  return [
    ...MANIFEST_WORKBOOK_INSTRUCTIONS,
    savedCases.length
      ? 'Saved cases in this study. The number is the saved identity. The label is not unique:'
      : 'No saved cases were available in this study when this file was created.',
    ...savedCases.map(({ id, label }) => `${id}: ${label}`),
  ];
};
